import React, { useRef, useEffect, useState } from 'react';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import './CustomRichTextEditor.css';

const CustomRichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  height = '300px',
  showToolbar = true,
  toolbarConfig = 'full', // 'basic', 'standard', 'full'
  onImageUpload, // Optional callback for custom image upload handling
}) => {
  const editorRef = useRef(null);
  const [showHtmlView, setShowHtmlView] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const savedRangeRef = useRef(null); // Store selection range before dropdown opens
  const [dropdownStates, setDropdownStates] = useState({
    'font-size': false,
    'font-color': false,
    'highlight': false,
    'heading': false,
  });

  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      if (showHtmlView) {
        setHtmlContent(value);
      } else {
        // Only update if content actually changed to avoid cursor jumping
        if (editorRef.current.innerHTML !== value) {
          editorRef.current.innerHTML = value || '';
        }
      }
    }
  }, [value, showHtmlView]);

  // Toolbar configurations
  const toolbarConfigs = {
    basic: ['bold', 'italic', 'underline', '|', 'unordered-list', 'ordered-list'],
    standard: [
      'bold', 'italic', 'underline', 'strike', '|',
      'heading', '|',
      'unordered-list', 'ordered-list', '|',
      'link', 'image', '|',
      'undo', 'redo'
    ],
    full: [
      'bold', 'italic', 'underline', 'strike', '|',
      'heading', '|',
      'font-size', 'font-color', 'highlight', '|',
      'unordered-list', 'ordered-list', '|',
      'align-left', 'align-center', 'align-right', '|',
      'link', 'image', '|',
      'table', '|',
      'undo', 'redo', '|',
      'html-view', '|',
      'clear'
    ]
  };

  const getToolbarButtons = () => {
    const config = toolbarConfigs[toolbarConfig] || toolbarConfigs.full;
    return config;
  };

  // Helper function to get a safe range within the editor
  const getEditorRange = () => {
    if (!editorRef.current) return null;

    // First check if we have a saved range (from before dropdown opened)
    if (savedRangeRef.current) {
      const savedRange = savedRangeRef.current;
      // Verify the saved range is still valid
      try {
        if (editorRef.current.contains(savedRange.startContainer) ||
          editorRef.current.contains(savedRange.startContainer.parentNode)) {
          return savedRange.cloneRange();
        }
      } catch (e) {
        // Range is invalid, clear it
        savedRangeRef.current = null;
      }
    }

    const selection = window.getSelection();
    let range = null;

    // Check if we have a valid selection within the editor
    if (selection.rangeCount > 0) {
      const currentRange = selection.getRangeAt(0);
      const startContainer = currentRange.startContainer;
      const endContainer = currentRange.endContainer;

      // Check if both start and end containers are within the editor
      const isStartInEditor = editorRef.current.contains(
        startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentNode : startContainer
      );
      const isEndInEditor = editorRef.current.contains(
        endContainer.nodeType === Node.TEXT_NODE ? endContainer.parentNode : endContainer
      );

      if (isStartInEditor && isEndInEditor) {
        range = currentRange.cloneRange();
      }
    }

    // If no valid range, create one at the end of the editor
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // Collapse to end
    }

    return range;
  };

  // Save the current selection before opening dropdown
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);
      const startContainer = range.startContainer;
      const endContainer = range.endContainer;

      // Check if selection is within editor
      const isStartInEditor = editorRef.current.contains(
        startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentNode : startContainer
      );
      const isEndInEditor = editorRef.current.contains(
        endContainer.nodeType === Node.TEXT_NODE ? endContainer.parentNode : endContainer
      );

      if (isStartInEditor && isEndInEditor) {
        savedRangeRef.current = range.cloneRange();
      }
    }
  };

  const applyHeading = (headingTag) => {
    if (disabled || !editorRef.current) return;

    // Ensure editor has focus
    editorRef.current.focus();

    // Get a safe range within the editor
    const range = getEditorRange();
    if (!range) return;

    // Set the selection to the range
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    // Apply the heading using formatBlock
    try {
      document.execCommand('formatBlock', false, headingTag);
      handleContentChange();
    } catch (e) {
      console.error('Error applying heading:', e);
      // Fallback: manually wrap selection in heading tag
      if (range.collapsed) {
        // If no selection, create a heading at cursor
        const heading = document.createElement(headingTag.replace(/[<>]/g, ''));
        heading.textContent = 'Heading';
        range.insertNode(heading);
        range.setStartAfter(heading);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        handleContentChange();
      } else {
        // Wrap selected content
        const contents = range.extractContents();
        const heading = document.createElement(headingTag.replace(/[<>]/g, ''));
        heading.appendChild(contents);
        range.insertNode(heading);
        handleContentChange();
      }
    }
  };

  const applyFontSize = (size) => {
    if (disabled || !editorRef.current) return;

    editorRef.current.focus();
    const range = getEditorRange();
    if (!range) return;

    // Clear saved range after using it
    savedRangeRef.current = null;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    // Always use custom implementation (execCommand creates deprecated <font> tags)
    // Map size numbers to actual font sizes
    const sizeMap = {
      '1': '10px',
      '2': '13px',
      '3': '16px',
      '4': '18px',
      '5': '24px',
      '6': '32px',
      '7': '48px'
    };

    const fontSize = sizeMap[size] || size + 'px';

    if (range.collapsed || range.toString().trim() === '') {
      // If no selection, wrap the current text node or create a span for future typing
      let container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        container = container.parentNode;
      }

      // Check if container already has font-size or is a span we can modify
      if (container.tagName === 'SPAN' && container.style.fontSize) {
        container.style.fontSize = fontSize;
      } else {
        // Create a span wrapper
        const span = document.createElement('span');
        span.style.fontSize = fontSize;
        const textNode = document.createTextNode('\u200B'); // Zero-width space
        span.appendChild(textNode);
        range.insertNode(span);
        range.setStart(span.firstChild, 1);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      handleContentChange();
    } else {
      // Wrap selected content - handle block elements properly
      try {
        const contents = range.extractContents();

        // If contents is empty or only whitespace, don't apply
        if (!contents.textContent || contents.textContent.trim() === '') {
          return;
        }

        const span = document.createElement('span');
        span.style.fontSize = fontSize;

        // Handle case where selection spans block elements
        if (contents.childNodes.length > 0) {
          const fragment = document.createDocumentFragment();
          Array.from(contents.childNodes).forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              const textSpan = document.createElement('span');
              textSpan.style.fontSize = fontSize;
              textSpan.appendChild(node.cloneNode(true));
              fragment.appendChild(textSpan);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              const blockSpan = document.createElement('span');
              blockSpan.style.fontSize = fontSize;
              blockSpan.appendChild(node.cloneNode(true));
              fragment.appendChild(blockSpan);
            } else {
              fragment.appendChild(node.cloneNode(true));
            }
          });
          span.appendChild(fragment);
        } else {
          span.appendChild(contents);
        }

        range.insertNode(span);

        // Move cursor after the span
        const newRange = document.createRange();
        newRange.setStartAfter(span);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleContentChange();
      } catch (e) {
        console.error('Error applying font size:', e);
      }
    }
  };

  const applyTextColor = (color) => {
    if (disabled || !editorRef.current) return;

    editorRef.current.focus();
    const range = getEditorRange();
    if (!range) return;

    // Clear saved range after using it
    savedRangeRef.current = null;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    // Always use custom implementation for better control and visibility
    if (range.collapsed || range.toString().trim() === '') {
      // If no selection, wrap the current text node or create a span for future typing
      let container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        container = container.parentNode;
      }

      // Check if container already has color or is a span we can modify
      if (container.tagName === 'SPAN' && container.style.color) {
        container.style.color = color;
      } else {
        // Create a span wrapper
        const span = document.createElement('span');
        span.style.color = color;
        const textNode = document.createTextNode('\u200B'); // Zero-width space
        span.appendChild(textNode);
        range.insertNode(span);
        range.setStart(span.firstChild, 1);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      handleContentChange();
    } else {
      // Wrap selected content - handle block elements properly
      try {
        const contents = range.extractContents();

        // If contents is empty or only whitespace, don't apply
        if (!contents.textContent || contents.textContent.trim() === '') {
          return;
        }

        const span = document.createElement('span');
        span.style.color = color;

        // Handle case where selection spans block elements
        if (contents.childNodes.length > 0) {
          // Clone and wrap each node
          const fragment = document.createDocumentFragment();
          Array.from(contents.childNodes).forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              const textSpan = document.createElement('span');
              textSpan.style.color = color;
              textSpan.appendChild(node.cloneNode(true));
              fragment.appendChild(textSpan);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              // For block elements, wrap their content
              const blockSpan = document.createElement('span');
              blockSpan.style.color = color;
              blockSpan.appendChild(node.cloneNode(true));
              fragment.appendChild(blockSpan);
            } else {
              fragment.appendChild(node.cloneNode(true));
            }
          });
          span.appendChild(fragment);
        } else {
          span.appendChild(contents);
        }

        range.insertNode(span);

        // Move cursor after the span
        const newRange = document.createRange();
        newRange.setStartAfter(span);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleContentChange();
      } catch (e) {
        console.error('Error applying text color:', e);
        // Fallback: try execCommand
        try {
          document.execCommand('foreColor', false, color);
          handleContentChange();
        } catch (e2) {
          console.error('Fallback also failed:', e2);
        }
      }
    }
  };

  const applyHighlight = (color) => {
    if (disabled || !editorRef.current) return;

    editorRef.current.focus();
    const range = getEditorRange();
    if (!range) return;

    // Clear saved range after using it
    savedRangeRef.current = null;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    // Always use custom implementation for better control and visibility
    if (range.collapsed || range.toString().trim() === '') {
      // If no selection, wrap the current text node or create a span for future typing
      let container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        container = container.parentNode;
      }

      // Check if container already has backgroundColor or is a span we can modify
      if (container.tagName === 'SPAN' && container.style.backgroundColor) {
        container.style.backgroundColor = color;
      } else {
        // Create a span wrapper
        const span = document.createElement('span');
        span.style.backgroundColor = color;
        const textNode = document.createTextNode('\u200B'); // Zero-width space
        span.appendChild(textNode);
        range.insertNode(span);
        range.setStart(span.firstChild, 1);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      handleContentChange();
    } else {
      // Wrap selected content - handle block elements properly
      try {
        const contents = range.extractContents();

        // If contents is empty or only whitespace, don't apply
        if (!contents.textContent || contents.textContent.trim() === '') {
          return;
        }

        const span = document.createElement('span');
        span.style.backgroundColor = color;

        // Handle case where selection spans block elements
        if (contents.childNodes.length > 0) {
          const fragment = document.createDocumentFragment();
          Array.from(contents.childNodes).forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              const textSpan = document.createElement('span');
              textSpan.style.backgroundColor = color;
              textSpan.appendChild(node.cloneNode(true));
              fragment.appendChild(textSpan);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              const blockSpan = document.createElement('span');
              blockSpan.style.backgroundColor = color;
              blockSpan.appendChild(node.cloneNode(true));
              fragment.appendChild(blockSpan);
            } else {
              fragment.appendChild(node.cloneNode(true));
            }
          });
          span.appendChild(fragment);
        } else {
          span.appendChild(contents);
        }

        range.insertNode(span);

        // Move cursor after the span
        const newRange = document.createRange();
        newRange.setStartAfter(span);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleContentChange();
      } catch (e) {
        console.error('Error applying highlight:', e);
        // Fallback: try execCommand
        try {
          document.execCommand('backColor', false, color);
          handleContentChange();
        } catch (e2) {
          console.error('Fallback also failed:', e2);
        }
      }
    }
  };

  const execCommand = (command, value = null) => {
    if (disabled) return;

    // Ensure editor has focus for all commands
    if (editorRef.current) {
      editorRef.current.focus();
    }

    if (command === 'heading') {
      applyHeading(value || '<h2>');
    } else if (command === 'font-size') {
      applyFontSize(value || '3');
    } else if (command === 'font-color') {
      applyTextColor(value || '#000000');
    } else if (command === 'highlight') {
      applyHighlight(value || '#ffff00');
    } else if (command === 'table') {
      insertTable();
    } else if (command === 'image') {
      insertImage();
    } else if (command === 'link') {
      insertLink();
    } else if (command === 'html-view') {
      toggleHtmlView();
    } else if (command === 'clear') {
      if (window.confirm('Clear all content?')) {
        editorRef.current.innerHTML = '';
        handleContentChange();
      }
    } else {
      const range = getEditorRange();
      if (range) {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
      document.execCommand(command, false, value);
      handleContentChange();
    }
  };

  const insertTable = () => {
    if (disabled) return;

    // Ensure editor has focus
    if (editorRef.current) {
      editorRef.current.focus();
    }

    const rows = prompt('Number of rows:', '3');
    const cols = prompt('Number of columns:', '3');

    if (rows && cols && !isNaN(rows) && !isNaN(cols)) {
      const table = document.createElement('table');
      table.style.borderCollapse = 'collapse';
      table.style.width = '100%';
      table.style.border = '1px solid #ddd';
      table.style.margin = '10px 0';

      const tbody = document.createElement('tbody');

      for (let i = 0; i < parseInt(rows); i++) {
        const tr = document.createElement('tr');
        for (let j = 0; j < parseInt(cols); j++) {
          const td = document.createElement('td');
          td.style.border = '1px solid #ddd';
          td.style.padding = '8px';
          td.style.minWidth = '50px';
          td.innerHTML = '&nbsp;';
          tr.appendChild(td);
        }
        tbody.appendChild(tr);
      }

      table.appendChild(tbody);

      // Get a safe range within the editor
      const range = getEditorRange();
      if (range) {
        // Insert a line break before table if needed
        const rangeStart = range.startContainer;
        if (rangeStart.nodeType === Node.TEXT_NODE && rangeStart.textContent.trim() !== '') {
          const br = document.createElement('br');
          range.insertNode(br);
          range.setStartAfter(br);
        }

        range.insertNode(table);

        // Move cursor after table
        const selection = window.getSelection();
        const newRange = document.createRange();
        newRange.setStartAfter(table);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      } else {
        // Fallback: append to editor
        editorRef.current.appendChild(table);
      }

      handleContentChange();
    }
  };

  const insertImage = () => {
    if (disabled) return;

    // Ensure editor has focus
    if (editorRef.current) {
      editorRef.current.focus();
    }

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      const file = input.files[0];
      if (file) {
        if (onImageUpload) {
          // Use custom upload handler
          onImageUpload(file, (imageUrl) => {
            insertImageAtCursor(imageUrl);
          });
        } else {
          // Default: convert to base64
          const reader = new FileReader();
          reader.onload = (e) => {
            insertImageAtCursor(e.target.result);
          };
          reader.readAsDataURL(file);
        }
      }
    };
  };

  const insertImageAtCursor = (imageUrl) => {
    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.maxWidth = '100%';
    img.style.height = 'auto';

    // Get a safe range within the editor
    const range = getEditorRange();
    if (range) {
      range.deleteContents();
      range.insertNode(img);

      // Move cursor after image
      const selection = window.getSelection();
      const newRange = document.createRange();
      newRange.setStartAfter(img);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } else {
      // Fallback: append to editor
      editorRef.current.appendChild(img);
    }

    handleContentChange();
  };

  const insertLink = () => {
    if (disabled) return;

    // Ensure editor has focus
    if (editorRef.current) {
      editorRef.current.focus();
    }

    const url = prompt('Enter URL:', 'https://');
    if (url) {
      // Get a safe range within the editor
      const range = getEditorRange();
      const selection = window.getSelection();

      // Check if there's selected text within the editor
      if (range && selection.toString() && editorRef.current.contains(selection.anchorNode)) {
        // Use execCommand for selected text
        document.execCommand('createLink', false, url);
      } else if (range) {
        // Insert link at cursor position
        const link = document.createElement('a');
        link.href = url;
        link.textContent = url;
        link.target = '_blank';
        range.deleteContents();
        range.insertNode(link);

        // Move cursor after link
        const newRange = document.createRange();
        newRange.setStartAfter(link);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      } else {
        // Fallback: append to editor
        const link = document.createElement('a');
        link.href = url;
        link.textContent = url;
        link.target = '_blank';
        editorRef.current.appendChild(link);
      }
      handleContentChange();
    }
  };

  // Function to format HTML with proper indentation
  const formatHtml = (html) => {
    if (!html) return '';

    // Simple and reliable string-based formatter
    let formatted = html.trim();

    // Step 1: Split HTML into tokens (tags and text)
    const tokens = [];
    let current = '';
    let inTag = false;

    for (let i = 0; i < formatted.length; i++) {
      const char = formatted[i];

      if (char === '<') {
        if (current.trim()) {
          tokens.push({ type: 'text', content: current.trim() });
          current = '';
        }
        inTag = true;
        current += char;
      } else if (char === '>') {
        current += char;
        tokens.push({ type: 'tag', content: current.trim() });
        current = '';
        inTag = false;
      } else {
        current += char;
      }
    }

    if (current.trim()) {
      tokens.push({ type: 'text', content: current.trim() });
    }

    // Step 2: Format tokens with proper indentation
    const result = [];
    let indentLevel = 0;
    const voidElements = ['br', 'hr', 'img', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'];

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      const indent = ' '.repeat(indentLevel * 2);

      if (token.type === 'tag') {
        const tagContent = token.content;
        const isClosingTag = tagContent.startsWith('</');
        const isOpeningTag = tagContent.startsWith('<') && !isClosingTag;

        if (isClosingTag) {
          // Decrease indent BEFORE adding closing tag
          indentLevel = Math.max(0, indentLevel - 1);
          result.push(' '.repeat(indentLevel * 2) + tagContent);
        } else if (isOpeningTag) {
          // Check if it's a void/self-closing element
          const tagMatch = tagContent.match(/^<(\w+)/);
          const tagName = tagMatch ? tagMatch[1].toLowerCase() : '';
          const isVoidElement = voidElements.includes(tagName);
          const isSelfClosing = tagContent.endsWith('/>') || isVoidElement;

          // Add opening tag with current indent
          result.push(indent + tagContent);

          // Only increase indent if it's not a void/self-closing element
          if (!isSelfClosing) {
            indentLevel++;
          }
        }
      } else if (token.type === 'text') {
        // Text content - use current indent level
        if (token.content) {
          result.push(indent + token.content);
        }
      }
    }

    return result.join('\n');
  };

  const toggleHtmlView = () => {
    if (showHtmlView) {
      // Switching from HTML view to rich text
      if (editorRef.current) {
        editorRef.current.innerHTML = htmlContent;
        handleContentChange();
      }
    } else {
      // Switching to HTML view - format the HTML with proper indentation
      const rawHtml = editorRef.current ? editorRef.current.innerHTML : '';
      const formattedHtml = formatHtml(rawHtml);
      setHtmlContent(formattedHtml);
    }
    setShowHtmlView(!showHtmlView);
  };

  const handleContentChange = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleHtmlChange = (e) => {
    const newHtml = e.target.value;
    setHtmlContent(newHtml);
    if (onChange) {
      onChange(newHtml);
    }
  };

  const handlePaste = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    // Handle paste events - clean up pasted content
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    const selection = window.getSelection();

    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    handleContentChange();
  };

  const renderToolbarButton = (command, index) => {
    if (command === '|') {
      return <div key={`separator-${index}`} className="toolbar-separator" />;
    }

    const buttonConfigs = {
      'bold': { icon: 'fas fa-bold', label: 'Bold', command: 'bold' },
      'italic': { icon: 'fas fa-italic', label: 'Italic', command: 'italic' },
      'underline': { icon: 'fas fa-underline', label: 'Underline', command: 'underline' },
      'strike': { icon: 'fas fa-strikethrough', label: 'Strikethrough', command: 'strikeThrough' },
      'heading': { icon: 'fas fa-heading', label: 'Heading', isDropdown: true },
      'font-size': { icon: 'fas fa-text-height', label: 'Font Size', isDropdown: true },
      'font-color': { icon: 'fas fa-palette', label: 'Text Color', isColorPicker: true },
      'highlight': { icon: 'fas fa-highlighter', label: 'Highlight', isColorPicker: true },
      'unordered-list': { icon: 'fas fa-list-ul', label: 'Bullet List', command: 'insertUnorderedList' },
      'ordered-list': { icon: 'fas fa-list-ol', label: 'Numbered List', command: 'insertOrderedList' },
      'align-left': { icon: 'fas fa-align-left', label: 'Align Left', command: 'justifyLeft' },
      'align-center': { icon: 'fas fa-align-center', label: 'Align Center', command: 'justifyCenter' },
      'align-right': { icon: 'fas fa-align-right', label: 'Align Right', command: 'justifyRight' },
      'link': { icon: 'fas fa-link', label: 'Insert Link', command: 'link' },
      'image': { icon: 'fas fa-image', label: 'Insert Image', command: 'image' },
      'table': { icon: 'fas fa-table', label: 'Insert Table', command: 'table' },
      'undo': { icon: 'fas fa-undo', label: 'Undo', command: 'undo', execCommand: () => document.execCommand('undo', false, null) },
      'redo': { icon: 'fas fa-redo', label: 'Redo', command: 'redo', execCommand: () => document.execCommand('redo', false, null) },
      'html-view': { icon: 'fas fa-code', label: 'HTML View', command: 'html-view' },
      'clear': { icon: 'fas fa-eraser', label: 'Clear', command: 'clear' },
    };

    const config = buttonConfigs[command];
    if (!config) return null;

    if (config.isDropdown) {
      if (command === 'heading') {
        return (
          <Dropdown
            key={`${command}-${index}`}
            as={ButtonGroup}
            show={dropdownStates[command]}
            onToggle={(isOpen) => {
              setDropdownStates(prev => ({ ...prev, [command]: isOpen }));
              if (isOpen) {
                saveSelection();
              }
            }}
          >
            <Button
              variant="outline-secondary"
              size="sm"
              title={config.label}
              disabled={disabled}
              onClick={() => execCommand('heading', '<h2>')}
            >
              <i className={config.icon}></i>
            </Button>
            <Dropdown.Toggle
              split
              variant="outline-secondary"
              size="sm"
              disabled={disabled}
              style={{ borderLeft: '1px solid #dee2e6', minWidth: '30px' }}
              aria-label="Toggle heading dropdown"
            />
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => {
                setDropdownStates(prev => ({ ...prev, [command]: false }));
                execCommand('heading', '<h1>');
              }}>Heading 1</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                setDropdownStates(prev => ({ ...prev, [command]: false }));
                execCommand('heading', '<h2>');
              }}>Heading 2</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                setDropdownStates(prev => ({ ...prev, [command]: false }));
                execCommand('heading', '<h3>');
              }}>Heading 3</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                setDropdownStates(prev => ({ ...prev, [command]: false }));
                execCommand('heading', '<h4>');
              }}>Heading 4</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                setDropdownStates(prev => ({ ...prev, [command]: false }));
                execCommand('heading', '<p>');
              }}>Normal</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      } else if (command === 'font-size') {
        return (
          <Dropdown
            key={`${command}-${index}`}
            as={ButtonGroup}
            show={dropdownStates[command]}
            onToggle={(isOpen) => {
              setDropdownStates(prev => ({ ...prev, [command]: isOpen }));
              if (isOpen) {
                // Save selection when dropdown opens
                saveSelection();
              }
            }}
          >
            <Button
              variant="outline-secondary"
              size="sm"
              title={config.label}
              disabled={disabled}
              onClick={() => execCommand('font-size', '3')}
            >
              <i className={config.icon}></i>
            </Button>
            <Dropdown.Toggle
              split
              variant="outline-secondary"
              size="sm"
              disabled={disabled}
              style={{ borderLeft: '1px solid #dee2e6', minWidth: '30px' }}
              aria-label="Toggle font size dropdown"
            />
            <Dropdown.Menu>
              {[1, 2, 3, 4, 5, 6, 7].map(size => (
                <Dropdown.Item key={size} onClick={() => {
                  // Close dropdown immediately
                  setDropdownStates(prev => ({ ...prev, [command]: false }));
                  setTimeout(() => {
                    if (editorRef.current) {
                      editorRef.current.focus();
                    }
                    execCommand('font-size', size.toString());
                  }, 100);
                }}>
                  {size}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        );
      }
    } else if (config.isColorPicker) {
      const colors = command === 'font-color'
        ? ['#000000', '#333333', '#666666', '#999999', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF']
        : ['#FFFF00', '#FFCC00', '#FF9900', '#FF6600', '#00FF00', '#00CCFF', '#FF00FF', '#FF0000'];

      return (
        <Dropdown
          key={`${command}-${index}`}
          as={ButtonGroup}
          show={dropdownStates[command]}
          onToggle={(isOpen) => {
            setDropdownStates(prev => ({ ...prev, [command]: isOpen }));
            if (isOpen) {
              // Save selection when dropdown opens
              saveSelection();
            }
          }}
        >
          <Button
            variant="outline-secondary"
            size="sm"
            title={config.label}
            disabled={disabled}
          >
            <i className={config.icon}></i>
          </Button>
          <Dropdown.Toggle
            split
            variant="outline-secondary"
            size="sm"
            disabled={disabled}
            style={{ borderLeft: '1px solid #dee2e6', minWidth: '30px' }}
            aria-label={`Toggle ${config.label} dropdown`}
          />
          <Dropdown.Menu>
            <div style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px', width: '200px' }}>
              {colors.map(color => (
                <div
                  key={color}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Close dropdown immediately
                    setDropdownStates(prev => ({ ...prev, [command]: false }));
                    // Small delay to ensure dropdown closes first
                    setTimeout(() => {
                      // Restore focus to editor before applying color
                      if (editorRef.current) {
                        editorRef.current.focus();
                      }
                      execCommand(command, color);
                    }, 100);
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    borderRadius: '2px'
                  }}
                  title={color}
                />
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      );
    } else {
      return (
        <Button
          key={`${command}-${index}`}
          variant="outline-secondary"
          size="sm"
          title={config.label}
          onClick={() => {
            if (config.execCommand) {
              config.execCommand();
              editorRef.current.focus();
              handleContentChange();
            } else {
              execCommand(config.command || command);
            }
          }}
          disabled={disabled}
        >
          <i className={config.icon}></i>
        </Button>
      );
    }
  };

  return (
    <div className="custom-rich-text-editor">
      {showToolbar && (
        <div className="editor-toolbar">
          {getToolbarButtons().map((command, index) => renderToolbarButton(command, index))}
        </div>
      )}

      {showHtmlView ? (
        <div className="html-editor-view">
          <textarea
            value={htmlContent}
            onChange={handleHtmlChange}
            placeholder={placeholder}
            disabled={disabled}
            style={{
              width: '100%',
              minHeight: height,
              fontFamily: 'monospace',
              fontSize: '13px',
              padding: '12px',
              border: '1px solid #ced4da',
              borderRadius: '0.375rem',
              resize: 'vertical'
            }}
          />
          <div className="html-view-footer">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={toggleHtmlView}
            >
              <i className="fas fa-edit me-1"></i>
              Switch to Rich Text
            </Button>
          </div>
        </div>
      ) : (
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleContentChange}
          onPaste={handlePaste}
          className="editor-content"
          style={{
            minHeight: height,
            padding: '12px',
            border: '1px solid #ced4da',
            borderRadius: '0.375rem',
            outline: 'none',
            overflowY: 'auto',
            backgroundColor: disabled ? '#e9ecef' : '#fff',
            cursor: disabled ? 'not-allowed' : 'text'
          }}
          data-placeholder={placeholder}
          suppressContentEditableWarning
        />
      )}
    </div>
  );
};

export default CustomRichTextEditor;
